import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import Caret from '../components/Caret';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated/gql';
import arrowUpRightIcon from '../icons/non-prefilled/16x16/arrowUpRight.svg';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import teamIcon from '../icons/non-prefilled/16x16/team.svg';
import thanksIcon from '../icons/non-prefilled/16x16/thanks.svg';
import EntitySelect from '../search/entitySearch/EntitySelect';
import { UIActions } from '../ui/uiSlice';
import UserRow from '../user/UserRow';
import styles from './ControlBarInsights.module.scss';
import PostInsights, { usePostCounts } from './PostInsights';
import postPolicyCheck, { PostPolicyAction } from './postPolicyCheck';
import { useUpdatePostOwner } from './sharing/postPermissionOperations';

const USER_FRAGMENT = gql(`
  fragment ControlBarUsers_user on User {
    id
    ...UserRow_user
  }
`);
type UserFragment = DocumentType<typeof USER_FRAGMENT>;

interface MetaButtonProps {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
}
const MetaButton = ({
  onClick,
  children,
  active,
  disabled,
}: MetaButtonProps) => (
  <div className={styles.metaButtonWrapper}>
    <button
      type="button"
      disabled={disabled}
      className={classNames(styles.metaButton, {
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
    >
      {children}
    </button>
  </div>
);

interface ControlBarUsersProps {
  users: (UserFragment & { removable?: boolean })[];
  onRemove?: (userId: string) => void;
}

const ControlBarUsers = ({ users, onRemove }: ControlBarUsersProps) => (
  <div className={styles.users}>
    {users.map((user) => (
      <div key={user.id} className={styles.user}>
        <div className={styles.userRow}>
          <UserRow user={user} />
        </div>
        {onRemove && user.removable && (
          <div className={styles.userMeta}>
            <MetaButton onClick={() => onRemove(user.id)}>
              <SVGIcon size={16}>{clearIcon}</SVGIcon>
            </MetaButton>
          </div>
        )}
      </div>
    ))}
  </div>
);

gql(`
  fragment ControlBarInsights_thankers on User {
    id
    ...AvatarStack_users
    ...ControlBarUsers_user
  }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBarInsights_post on Post {
    id
    owner {
      id
      ...UserRow_user
    }
    contributors {
      id
      ...AvatarStack_users
      ...ControlBarUsers_user
    }
    thankers {
      ...ControlBarInsights_thankers
    }
    insertedAt
    updatedAt
    ...postPolicyCheck_post
  }
`);

const THANK_POST_MUTATION = gql(`
  mutation postThank($id: ID!) {
    post: thankPost(id: $id) {
      id
      thankers {
        ...ControlBarInsights_thankers
      }
    }
  }
`);

const UNTHANK_POST_MUTATION = gql(`
  mutation postUnthank($id: ID!) {
    post: unthankPost(id: $id) {
      id
      thankers {
        ...ControlBarInsights_thankers
      }
    }
  }
`);

interface ControlBarInsightsProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
}

const ControlBarInsights = ({ post, close }: ControlBarInsightsProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const [ownerInputValue, setOwnerInputValue] = useState('');
  const postCounts = usePostCounts();
  const [rightContent, setRightContent] = useState<{
    type: 'owner' | 'contributors' | 'thankers';
    transformPosition: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const ownerRef = useRef<HTMLDivElement>(null);
  const contributorsRef = useRef<HTMLDivElement>(null);
  const thankersRef = useRef<HTMLDivElement>(null);

  const updateOwner = useUpdatePostOwner();

  const [thankMutation] = useMutation(THANK_POST_MUTATION);
  const [unthankMutation] = useMutation(UNTHANK_POST_MUTATION);

  const isThanker = post.thankers.some((t) => t.id === currentUser.id);

  const toggleThank = () => {
    const mutation = isThanker ? unthankMutation : thankMutation;

    mutation({
      variables: { id: post.id },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          ...post,
          thankers: !isThanker
            ? [...post.thankers, currentUser]
            : post.thankers.filter((u) => u.id !== currentUser.id),
        },
      },
    });
  };

  const getSetRightContent =
    (content: 'owner' | 'contributors' | 'thankers') =>
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (rightContent?.type === content) {
        setRightContent(null);
        return;
      }

      const activeSectionOffset = (
        {
          owner: ownerRef,
          contributors: contributorsRef,
          thankers: thankersRef,
        } as const
      )[content].current?.offsetTop;

      if (activeSectionOffset && containerRef.current) {
        setRightContent({
          type: content,
          transformPosition: `translateY(
            min(
              calc(
                ${activeSectionOffset}px + 100% - ${containerRef.current.clientHeight}px - 8px
                )
              , -16px
            )
          )`,
        });
      }
    };

  const suggestedOwnersSet = useMemo(
    () =>
      new Set(
        post.contributors
          .filter((c) => c.id !== post.owner.id)
          .slice(0, 5)
          .map((c) => c.id),
      ),
    [post.contributors, post.owner.id],
  );

  const canUpdateOwner = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  // TODO: filter hidden contributors once implemented on BE
  const shownContributors = post.contributors;

  return (
    <>
      <PopOverContainer noPadding width="auto">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className={styles.container}
          onClick={() => setRightContent(null)}
          ref={containerRef}
        >
          <div className={styles.section} ref={ownerRef}>
            <div className={styles.title}>
              owner
              <MetaButton
                active={rightContent?.type === 'owner'}
                disabled={!canUpdateOwner}
                onClick={getSetRightContent('owner')}
              >
                <SVGIcon size={14}>{editIcon}</SVGIcon>
              </MetaButton>
            </div>

            <UserRow link user={post.owner} avatarSize={32} />
          </div>
          <div className={styles.section} ref={contributorsRef}>
            <div className={styles.title}>
              contributors
              <MetaButton
                disabled={post.contributors.length === 0}
                active={rightContent?.type === 'contributors'}
                onClick={getSetRightContent('contributors')}
              >
                <Caret direction="right" size={12} />
              </MetaButton>
            </div>
            {shownContributors.length > 0 ? (
              <AvatarStack
                size={32}
                users={shownContributors}
                limit={9}
                link
                simpleCount
                spacing="tight"
              />
            ) : (
              <button
                onClick={getSetRightContent('contributors')}
                type="button"
                className={styles.actionButton}
              >
                <SVGIcon size={16}>{teamIcon}</SVGIcon>
              </button>
            )}
          </div>
          <div className={styles.section} ref={thankersRef}>
            <div className={styles.title}>
              thankers
              <MetaButton
                active={rightContent?.type === 'thankers'}
                disabled={post.thankers.length === 0}
                onClick={getSetRightContent('thankers')}
              >
                <Caret direction="right" size={12} />
              </MetaButton>
            </div>
            <div className={styles.thankers}>
              <AvatarStack
                size={32}
                users={post.thankers}
                limit={8}
                link
                simpleCount
                expandable
                spacing="tight"
              />
              <Tooltip
                content={
                  isThanker ? 'Remove your thanks' : 'Give thanks\nto the owner'
                }
                placement="top"
                trigger="hover"
              >
                <button
                  aria-label={isThanker ? 'Unthank' : 'Thank'}
                  onClick={toggleThank}
                  type="button"
                  className={classNames(styles.actionButton, {
                    [styles.isThanker]: isThanker,
                  })}
                >
                  <SVGIcon size={16}>{thanksIcon}</SVGIcon>
                </button>
              </Tooltip>
            </div>
          </div>
          <div
            className={classNames(styles.insightsSection, styles.insightGroup)}
          >
            <div className={styles.insightGroupItem}>
              <div className={styles.insightsTitle}>date created</div>
              <div>{moment(post.insertedAt).format('MMM D, YYYY')}</div>
            </div>
            <div className={styles.insightGroupItem}>
              <div className={styles.insightsTitle}>last updated</div>
              <div>{moment(post.updatedAt).format('MMM D, YYYY')}</div>
            </div>
          </div>
          <div className={classNames(styles.section, styles.insightGroup)}>
            <div className={styles.insightGroupItem}>
              <div className={styles.insightsTitle}>word count</div>
              <div>{postCounts.word?.toLocaleString()}</div>
            </div>
            <div className={styles.insightGroupItem}>
              <div className={styles.insightsTitle}>character count</div>
              <div>{postCounts.character?.toLocaleString()}</div>
            </div>
          </div>
          <div className={styles.section}>
            <Button
              variant="secondary"
              className={styles.openInsightsButton}
              onClick={() => {
                dispatch(
                  UIActions.openRightPanel(PostInsights, { id: post.id }),
                );
                close();
              }}
            >
              <span>Open insights</span>
              <SVGIcon size={14}>{arrowUpRightIcon}</SVGIcon>
            </Button>
          </div>
        </div>
      </PopOverContainer>
      {rightContent && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={styles.rightContent}
          onClick={(e) => e.stopPropagation()}
          style={{ transform: rightContent.transformPosition }}
        >
          {rightContent.type === 'owner' && (
            <EntitySelect
              menuIsOpen
              autoFocus
              config={{
                user: {
                  filter: (user) =>
                    post.owner.id !== user.id &&
                    (!!ownerInputValue ||
                      suggestedOwnersSet.size === 0 ||
                      suggestedOwnersSet.has(user.id)),
                },
              }}
              disableMenuPortal
              placeholder="Change the owner..."
              containerClassName={styles.ownerSelect}
              onChange={(result) => {
                if (result?.type !== 'user') return;
                const { user } = result;
                updateOwner(post, user);
                setRightContent(null);
              }}
              inputValue={ownerInputValue}
              onInputChange={setOwnerInputValue}
            />
          )}
          {rightContent.type === 'contributors' && (
            <ControlBarUsers users={post.contributors} />
          )}
          {rightContent.type === 'thankers' && post.thankers.length > 0 && (
            <ControlBarUsers
              users={post.thankers.map((user) => ({
                ...user,
                removable: user.id === currentUser.id,
              }))}
              onRemove={() => {
                toggleThank();
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ControlBarInsights;
